import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState
} from "react";

import { Trans, useTranslation } from "react-i18next";
import useWebSocket from "react-use-websocket-lite";

import { DialogWidth } from "@/components/Dialog/types/enums";
import { FILTER_KEYS } from "@/components/Filters/types/enums";
import InfoIconWithTooltip from "@/components/IconsWithTooltips/InfoIconWithTooltip/InfoIconWithTooltip";
import useActionsForSelection from "@/components/Table/hooks/useActionsForSelection";
import { ALERT_STATUS } from "@/context/alert/types";
import { VehicleDataDto } from "@/Pages/Vehicles/interfaces";
import RectangleLoader from "@/shared/components/loaders/RectangleLoader";

import useAxiosPrivate from "../../../../../../api/hooks/useAxiosPrivate";
import { ApiResources } from "../../../../../../api/resources";
import {
  ROUTES,
  useMediaQueries,
  VEHICLES_ACTIONS
} from "../../../../../../shared";
import DialogActions from "../../../../../../shared/components/buttons/DialogActions";
import useResponse from "../../../../../../shared/hooks/useResponse";
import Dialog from "../../../../../Dialog/Dialog";
import AssignSchedulesDialog from "./AssignSchedulesDialog/AssignSchedulesDialog";
import DownloadHistoryDialog from "./DownloadHistoryDialog/DownloadHistoryDialog";
import DownloadMonitoringDescription from "./DownloadMonitoringDescription/DownloadMonitoringDescription";
import DownloadMonitoringDialogContent from "./DownloadMonitoringDialogContent/DownloadMonitoringDialogContent";
import useDownloadStatusHistory from "./hooks/useDownloadStatusHistory";
import { DownloadStagesTypes, DownloadStatusesTypes } from "./types";
import {
  AssignSchedulesPayload,
  DownloadMonitoringVehicleData
} from "./types/interfaces";

interface DownloadMonitoringDialogProps {
  open: boolean;
  rowData: VehicleDataDto;
  onClose: () => void;
}

const DownloadMonitoringDialog = ({
  open,
  rowData,
  onClose
}: DownloadMonitoringDialogProps) => {
  const { fromMd, toMd } = useMediaQueries();
  const { getData, postData } = useAxiosPrivate();
  const { handleCommonError } = useResponse();
  const { getDownloadStatusHistory, downloadStatusHistory, isLoading } =
    useDownloadStatusHistory();

  const { applyBulkChangesToItems } = useActionsForSelection(ROUTES.Vehicles);

  const { t } = useTranslation();

  const [socketUrl, setSocketUrl] = useState<string | null>(null);
  const [lastJsonMessage, setLastJsonMessage] =
    useState<DownloadMonitoringVehicleData | null>(null);

  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [isAssignSchedulesOpen, setIsAssignSchedulesOpen] = useState(false);
  const [isLiveDataLoading, setIsLiveDataLoading] = useState(false);
  const [schedulesExists, setSchedulesExists] = useState(false);

  const isMounted = useRef<boolean>(false);
  const isHistoryMounted = useRef<boolean>(false);

  useWebSocket({
    url: socketUrl,
    shouldReconnect: true,
    retryOnError: true,
    onMessage(event) {
      try {
        const parsedMessage = JSON.parse(event.data);

        setLastJsonMessage(parsedMessage);
        setIsLiveDataLoading(false);
      } catch {}
    }
  });

  // Fetches a fresh websocket token and (re)opens the connection.
  // With `isReconnect` the current socket is closed first (url -> null),
  // and the new token guarantees the url differs from the previous one.
  const connectWebSocket = async (isReconnect = false) => {
    const wssBase = import.meta.env.VITE_BASE_URL_WSS;

    setIsLiveDataLoading(true);

    if (isReconnect) {
      setSocketUrl(null);
    }

    try {
      const { data } = await getData(ApiResources.VehicleSchedules);

      setSchedulesExists(Boolean(data.length));

      const response = await postData(ApiResources.CreateWebsocketToken);

      setSocketUrl(
        `${wssBase}/${ApiResources.VehiclesLiveStatus}?accessToken=${response.data.token}&VehicleId=${rowData?.id}`
      );
    } catch (error) {
      setIsLiveDataLoading(false);
      handleCommonError(error, t);
    }
  };

  useEffect(() => {
    // !isHistoryMounted.current is used to avoid unnecessary call to getDownloadStatusHistory
    if (open && !isHistoryMounted.current) {
      rowData && getDownloadStatusHistory(rowData.id, true);
      isHistoryMounted.current = true;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowData, open]);

  useEffect(() => {
    //!isMounted.current is used to avoid the warning of "WebSocket connection is being closed before it has been fully established".
    if (open && !isMounted.current) {
      connectWebSocket();
      isMounted.current = true;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // update download history when process is terminated or downloading stage is completed
  useEffect(() => {
    lastJsonMessage &&
      rowData &&
      checkIfHistoryShouldBeCalled(lastJsonMessage) &&
      getDownloadStatusHistory(rowData.id);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastJsonMessage, rowData]);

  const checkIfHistoryShouldBeCalled = useCallback(
    (lastJsonMessage: DownloadMonitoringVehicleData) => {
      const { status, stage } = lastJsonMessage.download || {};

      const isProcessTerminated =
        status === DownloadStatusesTypes.ProcessTerminated;
      const isStageCompleted = status === DownloadStatusesTypes.StageComplete;
      const isDownloadingStage = stage === DownloadStagesTypes.Downloading;

      return isProcessTerminated || (isDownloadingStage && isStageCompleted);
    },
    []
  );

  const handleCloseDialog = () => {
    onClose();
    setSocketUrl(null);

    // allow the connection and history to be initialised again on reopen
    isMounted.current = false;
    isHistoryMounted.current = false;
  };

  const handleOpenHistory = (isOpen: boolean) => {
    setIsHistoryOpen(isOpen);
  };

  const handleOpenAssignSchedules = (isOpen: boolean) => {
    setIsAssignSchedulesOpen(isOpen);
  };

  const handleAssignSchedulesSubmit = async (
    payload: AssignSchedulesPayload | null,
    setIsLoading: Dispatch<SetStateAction<boolean>>
  ) => {
    try {
      if (payload) {
        const scheduleCount = payload?.[FILTER_KEYS.ScheduleIds]?.length;

        const alertTitle =
          scheduleCount === 1
            ? t("Alert##successfully assigned schedules##single")
            : t("Alert##successfully assigned schedules##multiple");

        const handleClose = () => {
          handleOpenAssignSchedules(false);
          connectWebSocket(true);
        };

        await applyBulkChangesToItems(
          ApiResources.VehicleSchedulesBulkApply,
          payload,
          VEHICLES_ACTIONS.AssignSchedules,
          setIsLoading,
          handleClose,
          postData,
          { status: ALERT_STATUS.Success, title: alertTitle }
        );
      }
    } catch (error) {
      setIsLoading(false);
      handleCommonError(error, t);
    }
  };

  const vehicleNumber = rowData?.number ?? "";

  return (
    <>
      {rowData && (
        <Dialog
          title={t(`Dialog##download status dialog title`, {
            vehicleNumber
          })}
          titleTooltip={
            <InfoIconWithTooltip
              title={
                <Trans i18nKey="Dialog##download monitoring dialog tooltip" />
              }
              iconStyle={{ width: "24px", height: "24px" }}
            />
          }
          description={
            isLiveDataLoading ? (
              <RectangleLoader height="20px" width="100%" />
            ) : (
              <DownloadMonitoringDescription
                liveStatusData={
                  lastJsonMessage as DownloadMonitoringVehicleData
                }
              />
            )
          }
          open={open}
          close={handleCloseDialog}
          extendToMaxWidth={toMd || undefined}
          isActionsSeparator
          actions={
            <DialogActions
              onClose={handleCloseDialog}
              isLoading={false}
              hideAcceptButton
              isreadyToConfirm
              cancelButtonText={t("Button##close")}
            />
          }
          isTitleSeparator
          width={DialogWidth.Dialog920px}
          customChildrenStyle={{
            scrollbarGutter: "stable",
            padding: fromMd ? "16px 16px 16px 24px" : "16px 12px 16px 24px"
          }}
        >
          <DownloadMonitoringDialogContent
            schedulesExists={schedulesExists}
            isLiveDataLoading={isLiveDataLoading}
            liveStatusData={lastJsonMessage as DownloadMonitoringVehicleData}
            handleOpenHistory={handleOpenHistory}
            handleOpenAssignSchedules={handleOpenAssignSchedules}
          />
        </Dialog>
      )}

      <DownloadHistoryDialog
        vehicleNumber={vehicleNumber}
        downloadHistoryData={downloadStatusHistory}
        isHistoryLoading={isLoading || !lastJsonMessage}
        isOpen={isHistoryOpen}
        onClose={() => handleOpenHistory(false)}
      />

      {isAssignSchedulesOpen && (
        <AssignSchedulesDialog
          vehicleId={rowData?.id ?? ""}
          vehicleNumber={vehicleNumber}
          isOpen={isAssignSchedulesOpen}
          onClose={() => handleOpenAssignSchedules(false)}
          handleSubmit={handleAssignSchedulesSubmit}
        />
      )}
    </>
  );
};

export default DownloadMonitoringDialog;
